import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import App from './App';
import './styles/global.css';
import { initIap } from './lib/iap';
import { EntitlementProvider } from './lib/entitlementContext';

const debugOverlayEnabled =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_OVERLAY === '1';

/* Native shell bootstrap — status bar style and splash dismissal.
 * No-op in the web/PWA build because `Capacitor.isNativePlatform()`
 * returns false there. */
if (Capacitor.isNativePlatform()) {
  // White content on our dark theme. iOS auto-detection is usually
  // correct, but pinning the style here protects against future
  // iOS versions changing the heuristic.
  StatusBar.setStyle({ style: Style.Light }).catch(() => {
    /* older iOS or no permission — ignore. */
  });
}

// Visible error reporting for local debugging. Store builds keep errors out of
// the UI so reviewers and users never see stack traces or technical internals.
function showError(label: string, err: unknown) {
  if (!debugOverlayEnabled) {
    console.error(label, err);
    const root = document.getElementById('root');
    const fallback = `
      <main style="
        min-height: 100dvh; display: grid; place-items: center; padding: 24px;
        background: #000; color: rgba(245,245,247,0.86);
        font: 15px/1.5 -apple-system, BlinkMacSystemFont, sans-serif;
        text-align: center;
      ">
        <div>
          <div style="font-weight: 700; margin-bottom: 8px;">CADtomie Intraop could not start.</div>
          <div style="color: rgba(245,245,247,0.62);">Please close and reopen the app.</div>
        </div>
      </main>`;
    if (root && !root.hasChildNodes()) root.innerHTML = fallback;
    return;
  }

  const root = document.getElementById('root');
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}\n\n${err.stack ?? ''}`
      : String(err);
  const html = `
    <div style="
      position: fixed; inset: 0; padding: 20px; overflow: auto;
      background: #1a0a0a; color: #ffb4b4;
      font: 13px/1.5 -apple-system, monospace;
      white-space: pre-wrap; word-break: break-word;
      z-index: 99999;
    ">
      <div style="font-weight:700;font-size:15px;color:#ff6b6b;margin-bottom:10px">
        ${label}
      </div>
      <div>${msg.replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'))}</div>
      <div style="margin-top:14px;color:#888;font-size:11px">
        UA: ${navigator.userAgent}
      </div>
    </div>`;
  if (root) root.innerHTML = html;
  else document.body.insertAdjacentHTML('beforeend', html);
}

if (debugOverlayEnabled) {
  window.addEventListener('error', (e) => showError('Uncaught error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) =>
    showError('Unhandled promise rejection', e.reason),
  );
}

class Boundary extends React.Component<
  { children: React.ReactNode },
  { err: unknown }
> {
  state = { err: null as unknown };
  static getDerivedStateFromError(err: unknown) {
    return { err };
  }
  componentDidCatch(err: unknown) {
    showError('React render error', err);
  }
  render() {
    return this.state.err ? null : this.props.children;
  }
}

try {
  const container = document.getElementById('root');
  if (!container) throw new Error('Root container missing');

  // Fire-and-forget IAP bootstrap. We intentionally don't `await` this
  // before rendering: the EntitlementProvider seeds itself from the
  // localStorage cache so the very first frame already has the right
  // Pro state, and the real CustomerInfo fetch then patches in once
  // RevenueCat responds. Blocking the splash on the network round-trip
  // would mean a black-on-black flash for users on slow connections.
  void initIap().catch((e) => showError('IAP init failed', e));

  createRoot(container).render(
    <React.StrictMode>
      <Boundary>
        <BrowserRouter>
          <EntitlementProvider>
            <App />
          </EntitlementProvider>
        </BrowserRouter>
      </Boundary>
    </React.StrictMode>,
  );

  /* Hide the native splash AFTER the browser has actually painted
   * the first frame. Double-rAF is the cheapest reliable signal: the
   * inner callback runs in the frame *after* the one React committed,
   * so the splash fades into already-visible UI instead of into a
   * blank webview. No-op on web. */
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 220 }).catch(() => {});
      });
    });
  }
} catch (e) {
  showError('Bootstrap error', e);
}
